import React, { useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../../context/AuthContext";
import { gameApi } from "../../../lib/api";
import { launchGameById, launchHoldemGame, launchProviderLobby } from "../../../lib/gameLaunch";
import {
  createFavoriteGameItem,
  createFavoriteProviderItem,
  FAVORITES_UPDATED_EVENT,
  getFavoriteGames,
  toggleFavoriteGame,
} from "../../../utils/favoriteGames";
import {
  getRecentPlayAccountKey,
  getRecentPlayHistory,
  getProfileRecentPlayHistory,
  mergeRecentPlayRows,
  normalizeRecentPlayRows,
} from "../../../utils/recentPlayHistory";
import FavoriteToggleButton from "./FavoriteToggleButton";
import { StyledHomeBackendSections } from "./StyledHomeBackendSections";

const DISPLAY_LIMIT = 8;

const buildVendorFromRecent = (item) => ({
  vendor_name: item?.vendorName,
  vendor_name_kr: item?.title,
  lobby_id: item?.lobbyId,
  vendor_list: {
    lobby_id: item?.lobbyId,
    thumbnails: item?.image,
  },
  image: item?.image,
  game_type: item?.gameType,
});

const buildFavoriteFromRecent = (item) => {
  if (item?.kind === "provider" || item?.kind === "holdem-provider") {
    return createFavoriteProviderItem(buildVendorFromRecent(item), {
      label: item.title,
      gameType: item.gameType,
      meta: item.meta || "최근 실행",
      kind: item.kind,
    });
  }

  return createFavoriteGameItem(
    {
      game_id: item?.gameId,
      vendor: item?.vendorName,
      image: item?.image,
    },
    {
      label: item?.title,
      gameType: item?.gameType || "slots",
      meta: item?.meta || "최근 실행",
      image: item?.image,
    }
  );
};

const dispatchAuthRequired = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("crypto-auth-required", {
      detail: { modalOption: "login", reason: "favorite" },
    })
  );
};

const RecentPlayCard = ({
  item,
  index,
  isFavorite,
  isLaunching,
  onFavoriteToggle,
  onLaunch,
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const hasImage = item.image && !imageFailed;

  return (
    <div className="backend-card-shell">
      <button
        type="button"
        className={`backend-card-link backend-card-button recent-play-card-wrap${
          item.disabled ? " is-disabled" : ""
        }`}
        onClick={() => onLaunch(item)}
        disabled={item.disabled}
        aria-label={`${item.title} 계속 플레이`}
      >
        <article className={`backend-card recent-play-card ${hasImage ? "has-image" : "no-image"}`}>
          {hasImage ? (
            <img
              src={item.image}
              alt={item.title}
              loading={index < 4 ? "eager" : "lazy"}
              onError={() => setImageFailed(true)}
            />
          ) : null}
          <div className="backend-card-title">
            <strong>{item.title}</strong>
          </div>
          <div className="backend-card-hover-layer">
            <span className="backend-card-play" aria-hidden="true" />
            <strong>{isLaunching ? "실행 중" : item.title}</strong>
          </div>
        </article>
        <span className="recent-play-meta">
          <span className="live-dot" />
          {item.meta || "최근 실행"}
        </span>
      </button>
      <FavoriteToggleButton
        active={isFavorite}
        label={item.title}
        onToggle={() => onFavoriteToggle?.(item)}
      />
    </div>
  );
};

const HomeRecentPlaySection = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [launchingKey, setLaunchingKey] = useState("");
  const [favoriteKeys, setFavoriteKeys] = useState(() => new Set());
  const backendHistoryAccountRef = useRef("");
  const accountKey = useMemo(() => getRecentPlayAccountKey(user), [user]);

  useEffect(() => {
    if (!user?.isAuthenticated || !accountKey) {
      setItems([]);
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;

    const readLocalAndProfile = () =>
      mergeRecentPlayRows(
        getRecentPlayHistory(accountKey),
        getProfileRecentPlayHistory(user)
      );

    const refresh = () => {
      if (isMounted) setItems(readLocalAndProfile());
    };

    const loadBackendHistory = async () => {
      if (backendHistoryAccountRef.current === accountKey) {
        refresh();
        setIsLoading(false);
        return;
      }

      backendHistoryAccountRef.current = accountKey;
      setIsLoading(true);
      refresh();

      try {
        const response = await gameApi.history.getGameHistory({ page: 1, limit: DISPLAY_LIMIT });
        if (!isMounted) return;

        const payload = response?.data;
        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.rows)
          ? payload.rows
          : Array.isArray(payload?.data)
          ? payload.data
          : [];

        setItems(
          mergeRecentPlayRows(
            normalizeRecentPlayRows(rows, "플레이 기록"),
            readLocalAndProfile()
          )
        );
      } catch {
        if (isMounted) refresh();
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadBackendHistory();

    const handleRecentUpdate = (event) => {
      if (!event?.detail?.accountKey || event.detail.accountKey === accountKey) {
        refresh();
      }
    };

    const handleStorage = (event) => {
      if (!event.key || event.key.endsWith(`:${accountKey}`)) {
        refresh();
      }
    };

    window.addEventListener("crypto-recent-play-updated", handleRecentUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      isMounted = false;
      window.removeEventListener("crypto-recent-play-updated", handleRecentUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [accountKey, user]);

  useEffect(() => {
    if (!accountKey) {
      setFavoriteKeys(new Set());
      return undefined;
    }

    const refreshFavorites = () => {
      setFavoriteKeys(new Set(getFavoriteGames(accountKey).map((item) => item.key)));
    };

    refreshFavorites();

    const handleFavoritesUpdated = (event) => {
      if (!event?.detail?.accountKey || event.detail.accountKey === accountKey) {
        refreshFavorites();
      }
    };

    const handleStorage = (event) => {
      if (!event.key || event.key.endsWith(`:${accountKey}`)) {
        refreshFavorites();
      }
    };

    window.addEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [accountKey]);

  const visibleItems = items.slice(0, DISPLAY_LIMIT);

  const handleLaunch = async (item) => {
    if (!item || item.disabled) return;
    setLaunchingKey(item.key);
    try {
      if (item.kind === "holdem-provider" || item.gameType === "holdem") {
        await launchHoldemGame(buildVendorFromRecent(item), {
          label: item.title,
          gameType: "holdem",
          meta: item.meta,
        });
        return;
      }

      if (item.kind === "provider") {
        await launchProviderLobby(buildVendorFromRecent(item), {
          label: item.title,
          gameType: item.gameType,
          meta: item.meta,
        });
        return;
      }

      await launchGameById(item.gameId, {
        label: item.title,
        recentPlay: item,
      });
    } finally {
      setLaunchingKey("");
    }
  };

  const handleFavoriteToggle = (item) => {
    if (!user?.isAuthenticated || !accountKey) {
      dispatchAuthRequired();
      return;
    }

    const result = toggleFavoriteGame(accountKey, buildFavoriteFromRecent(item));
    setFavoriteKeys(new Set(result.rows.map((row) => row.key)));
  };

  if (!user?.isAuthenticated) return null;

  return (
    <StyledHomeBackendSections className="home-recent-play-wrapper">
      <section className="backend-section recent-play-section">
        <div className="backend-section-header">
          <div className="backend-section-title">
            <span className="backend-section-icon recent-play-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path fill="none" d="M6.75 12H16" />
                <path fill="none" d="M12.25 7.5L16.75 12L12.25 16.5" />
              </svg>
            </span>
            <div>
              <div className="backend-section-name">계속 플레이</div>
            </div>
          </div>
        </div>

        {isLoading && visibleItems.length === 0 ? (
          <div className="backend-card-rail recent-play-rail is-skeleton-rail" aria-hidden="true">
            {Array.from({ length: 4 }).map((_, index) => (
              <article className="backend-card skeleton-card" key={`recent-skeleton-${index}`}>
                <span className="skeleton-title" />
              </article>
            ))}
          </div>
        ) : visibleItems.length ? (
          <div className="backend-card-rail recent-play-rail">
            {visibleItems.map((item, index) => (
              <RecentPlayCard
                key={item.key}
                item={item}
                index={index}
                isFavorite={favoriteKeys.has(buildFavoriteFromRecent(item).key)}
                isLaunching={launchingKey === item.key}
                onFavoriteToggle={handleFavoriteToggle}
                onLaunch={handleLaunch}
              />
            ))}
          </div>
        ) : (
          <div className="section-state recent-play-empty">
            게임을 한 번 실행하면 여기에 최근 플레이가 표시됩니다.
          </div>
        )}
      </section>
    </StyledHomeBackendSections>
  );
};

export default HomeRecentPlaySection;
